import { Link } from "react-router-dom";
import { MapPin, Star, ImageOff } from "lucide-react";
import { Card } from "./ui/card";
import { CATEGORIES } from "../lib/categories";

export default function PlaceCard({ place, onHover, onLeave }) {
  const cat = CATEGORIES[place.category] || CATEGORIES.restaurant;
  const hasImage = !!place.image_url;

  // Compose category-specific secondary line
  const detail = (() => {
    if (place.category === "hotel" && place.stars) return `${place.stars}★ · ${place.subtype}`;
    if (place.category === "rental")
      return [place.subtype, place.bedrooms && `${place.bedrooms} ch.`, place.monthly_rent]
        .filter(Boolean)
        .join(" · ");
    if (place.category === "car_sale")
      return [place.brand, place.model, place.year, place.sale_price]
        .filter(Boolean)
        .join(" · ");
    if (place.category === "car_rental")
      return [place.brand, place.model, place.daily_rate && `${place.daily_rate}/jour`]
        .filter(Boolean)
        .join(" · ");
    return place.subtype;
  })();

  return (
    <Link to={`/place/${place.id}`} data-testid={`place-card-${place.id}`}>
      <Card
        onMouseEnter={() => onHover && onHover(place.id)}
        onMouseLeave={() => onLeave && onLeave()}
        className="group overflow-hidden rounded-2xl border border-brand-line bg-white hover:shadow-[0_16px_48px_rgba(45,42,38,0.08)] hover:-translate-y-1 transition-all duration-300 cursor-pointer"
      >
        <div
          className="aspect-[16/10] overflow-hidden relative"
          style={{ backgroundColor: hasImage ? "transparent" : `${cat.color}12` }}
          data-testid={hasImage ? "place-card-image" : "place-card-placeholder"}
        >
          {hasImage ? (
            <img
              src={place.image_url}
              alt={place.name}
              loading="lazy"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-center px-4">
              <div
                className="w-12 h-12 rounded-2xl flex items-center justify-center text-white shadow-sm"
                style={{ backgroundColor: cat.color }}
                aria-hidden="true"
              >
                <ImageOff className="w-5 h-5" strokeWidth={1.6} />
              </div>
              <p className="text-[11px] uppercase tracking-[0.15em] font-semibold text-brand-inkMuted">
                Photo bientôt disponible
              </p>
            </div>
          )}
          <div
            className="absolute top-3 left-3 px-2.5 py-1 rounded-full text-xs font-semibold text-white shadow-sm"
            style={{ backgroundColor: cat.color }}
          >
            {cat.singular}
          </div>
          {place.price_range && (
            <div className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-white/90 backdrop-blur-sm text-xs font-semibold text-brand-ink">
              {place.price_range}
            </div>
          )}
        </div>
        <div className="p-5">
          <div className="flex items-start justify-between gap-3 mb-1.5">
            <h3 className="font-heading font-semibold text-lg text-brand-ink leading-tight line-clamp-1">
              {place.name}
            </h3>
            <div className="flex items-center gap-1 shrink-0">
              <Star className="w-4 h-4 fill-brand-terracotta text-brand-terracotta" strokeWidth={1.5} />
              <span className="text-sm font-semibold text-brand-ink">
                {place.avg_rating?.toFixed(1) || "—"}
              </span>
              <span className="text-xs text-brand-inkMuted">({place.review_count || 0})</span>
            </div>
          </div>
          {detail && (
            <div
              className="text-xs uppercase tracking-[0.15em] font-semibold mb-2 line-clamp-1"
              style={{ color: cat.color }}
            >
              {detail}
            </div>
          )}
          <div className="flex items-center gap-1.5 text-sm text-brand-inkSoft">
            <MapPin className="w-3.5 h-3.5 text-brand-inkMuted" />
            <span className="line-clamp-1">
              {place.city}
              {place.address && ` · ${place.address}`}
            </span>
          </div>
        </div>
      </Card>
    </Link>
  );
}
